import {
  editGroupTemplate,
  getGroupTemplateData,
  deleteGroupTemplate,
  editSubmitGroupTemplate,
  getGroupTemplate
} from 'api/endpoints/SmsCategory';
import useApi from 'hooks/useApi';
import { jwtDecode } from 'jwt-decode';
import { debounce, get, isEmpty } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import ReportTable from './ReportTable';
import {
  Button,
  Card,
  CardBody,
  Col,
  Modal,
  OverlayTrigger,
  Row,
  Tooltip
} from 'react-bootstrap';
import FalconCardHeader from 'components/common/FalconCardHeader';
import PropTypes from 'prop-types';
import DynamicForm from 'screens/common/dynamicForm';
import { ConfirmationModal } from 'screens/common/ConfirmationModal';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter } from '@fortawesome/free-solid-svg-icons';
import CardDropdown from 'components/common/CardDropdown';
import * as XLSX from 'xlsx';
import { formSubmissionToast } from 'helpers/utils';
import { getTenantFromToken } from 'utils/routeUtils';

function GroupTemplate({ created }) {
  const tenant = getTenantFromToken();

  const { request: fetchData, data } = useApi(getGroupTemplateData);
  const editGroupTemplateForm = useApi(editGroupTemplate);
  const deleteSmsTemplateAction = useApi(deleteGroupTemplate);
  const editSubmitForm = useApi(editSubmitGroupTemplate);
  const getGroupTemplateForm = useApi(getGroupTemplate);

  const [tableData, setTableData] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [formValues, setFormValues] = useState({});
  const [editRowId, setEditRowId] = useState(null); // Track the ID of the row being edited
  const [isLoading, setIsLoading] = useState(false);
  const [pageSize, setPageSize] = useState(25);
  const [pageIndex, setPageIndex] = useState(0);
  const [rowCount, setRowCount] = useState(0);
  const [canNextPage, setCanNextPage] = useState(true);
  const [filters, setFilters] = useState({});
  const [showFilter, setShowFilter] = useState(false);
  const [smsTemplateCount, setSmsTemplateCount] = useState(0);
  const [globalfilters, setGlobalFilters] = useState({
    search_text: ''
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [exportdata, setExportData] = useState(null);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [exportKey, setExportKey] = useState(null);

  // Fetch table data
  const fetchTableData = () => {
    const limit = pageSize;
    const cursorValue = pageIndex * pageSize;
    fetchData({
      cursor: cursorValue,
      limit,
      tenant,
      filter: globalfilters.search_text
    });
  };

  useEffect(() => {
    fetchTableData();
  }, [pageIndex, pageSize, globalfilters, created]);

  // useEffect(() => {
  //   if (setFetchCallback) {
  //     setFetchCallback(fetchData); // Register the fetchData function in the parent
  //   }
  // }, [setFetchCallback]);

  // useEffect(() => {
  //   if (setFetchCallback) {
  //     setFetchCallback(fetchTableData);
  //   }
  // }, [setFetchCallback]);

  useEffect(() => {
    const tabledata = get(data, 'results', []);
    setExportData(tabledata?.data);
    setRowCount(get(data, 'results.total', 0));
    setTotalRowCount(get(data, 'results.total', 0));
    setCanNextPage(tabledata?.remaining_records > 0);
    setTableData(tabledata);
    setExportKey(get(data, 'results.export_key', ''));
  }, [data]);

  const handleActionClick = (action, rowData) => {
    const id = rowData?.id;

    if (action === 'edit' && id) {
      setEditRowId(id); // Track the row ID being edited
      setIsLoading(true); // Start loading state
      editGroupTemplateForm.request({ tenant, id });
    } else if (action === 'delete' && id) {
      const selectedTemplate = tableData?.data.find(
        template => template.id === id
      );
      const templateCount = selectedTemplate.template_count;
      setSmsTemplateCount(templateCount);
      setSelectedTemplateId(id);
      setShowDeleteModal(true);
    }
  };

  const handleDeleteConfirm = async id => {
    if (id) {
      // Call the delete function
      try {
        const res = await deleteGroupTemplate({ tenant, id });
        handleCloseModal();
        formSubmissionToast(res, toast, 'Category');
        fetchTableData();
      } catch (err) {
        formSubmissionToast(err, toast, 'Category');
      }
    }
    setShowDeleteModal(false); // Close modal after confirmation
  };

  useEffect(() => {
    getGroupTemplateForm.request({ tenant });
  }, [tenant]);

  useEffect(() => {
    const formdata = get(getGroupTemplateForm.data, 'results', {});
    const response = get(editGroupTemplateForm.data, 'results', {});
    if (response && !isEmpty(response)) {
      const updatedFormData = { ...formdata };
      updatedFormData.fields = updatedFormData?.fields?.map(field => {
        if (Object.prototype.hasOwnProperty.call(response, field.id)) {
          return {
            ...field,
            defaultValue: response[field.id]
          };
        }
        return field;
      });

      setFormValues(updatedFormData); // Update form values
      setIsLoading(false); // Stop loading state
      setShowModal(true); // Open modal after data is ready
    }
  }, [editGroupTemplateForm.data, getGroupTemplateForm.data]);

  const handleCloseModal = () => {
    setShowModal(false);
    setFormValues({}); // Clear form values when modal is closed
  };

  const handleFormSubmit = async data => {
    const combinedData = { ...data }; // Include row ID and tenant
    try {
      const response = await editSubmitGroupTemplate({
        id: editRowId,
        tenant,
        combinedData
      });
      formSubmissionToast(response, toast, 'Category');
      handleCloseModal();
      fetchTableData();
    } catch (err) {
      formSubmissionToast(err, toast, 'Category');
    }
  };

  useEffect(() => {
    const response = get(editSubmitForm.data, 'results', {});
    fetchTableData(); // Refresh table to show changes
  }, [editSubmitForm.data]);

  const handleDownload = () => {
    const filename = `SMS_Category.xlsx`; // Excel file extension
    const excelData = convertToExcelData(exportdata, filename); // Convert to Excel data format
    downloadExcel(excelData, filename);
  };

  const convertToExcelData = (data, filename) => {
    if (!data || data.length === 0) return [];
    // Generate timestamp
    const now = new Date();
    const timestamp = now
      .toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      })
      .replace(',', ' at');
    // Create readable title
    const readableName = filename
      .replace('.xlsx', '')
      .replace(/[_-]/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
    const reportTitle = `${readableName} – Generated on ${timestamp} from the FARMSIO Application`;
    // Extract headers from the first data item
    const headers = Object.keys(data[0]).filter(
      key => key !== 'id' && key !== 'action'
    ); // filter used to avoid uuid in the excel report
    // Map the data rows
    const dataRows = data.map(item =>
      headers.map(header => item[header] ?? '')
    );
    // Combine headers and data rows into a format that xlsx library expects
    const excelData = [
      [reportTitle], // Title
      [], // Empty row
      headers.map(h =>
        h.replace(/[_-]/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
      ), // Formatted headers
      ...dataRows
    ];
    return excelData;
  };

  const downloadExcel = (excelData, filename) => {
    // Create a new worksheet from the excel data
    const ws = XLSX.utils.aoa_to_sheet(excelData);

    // Optionally merge the title cell across columns
    let merge = [];
    if (excelData.length >= 3 && excelData[2].length > 0) {
      merge = [{ s: { r: 0, c: 0 }, e: { r: 0, c: excelData[2].length + 4 } }];
    }

    ws['!merges'] = merge;

    // Create a new workbook with the worksheet
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'SMS Category');

    // Write the Excel file to a Blob and download it
    XLSX.writeFile(wb, filename);
  };

  const handleFilterToggle = () => {
    setShowFilter(!showFilter);
  };

  return (
    <div>
      <Row>
        <Col>
          <Card>
            <FalconCardHeader
              title="SMS Category"
              titleTag="h5"
              light={true}
              endEl={
                <div className="d-flex justify-content-end my-auto">
                  <CardDropdown
                    toggleFilters={handleFilterToggle}
                    onExport={handleDownload}
                    exportKey={exportKey}
                  />
                </div>
              }
            />
            <CardBody>
              {tableData && (
                <ReportTable
                  data={tableData?.data}
                  key="group_template"
                  pageSize={pageSize}
                  setPageSize={setPageSize}
                  pageIndex={pageIndex}
                  setPageIndex={setPageIndex}
                  rowCount={rowCount}
                  setRowCount={setRowCount}
                  canNextPage={canNextPage}
                  filters={filters}
                  showFilter={showFilter}
                  totalRowCount={totalRowCount}
                  setFilters={setFilters}
                  handleAction={handleActionClick} // Handle edit and delete actions
                  setGlobalFilters={setGlobalFilters}
                  setExportData={setExportData}
                />
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit SMS Category</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!isEmpty(formValues) && formValues?.fields && (
            <DynamicForm
              data={formValues}
              modalType="lg"
              onSubmit={handleFormSubmit}
            />
          )}
        </Modal.Body>
      </Modal>
      <ConfirmationModal
        showModal={showDeleteModal}
        handleClose={() => setShowDeleteModal(false)}
        handleConfirm={handleDeleteConfirm}
        templateId={selectedTemplateId}
        actionType="delete"
        actionModel="group_template"
        smsTemplateCount={smsTemplateCount}
        title="SMS Category"
      />
    </div>
  );
}

GroupTemplate.propTypes = {
  handleAction: PropTypes.func,
  setFetchCallback: PropTypes.func,
  created: PropTypes.func
};

export default GroupTemplate;
